/**
 * app-routes.ts — single source of truth for all route metadata.
 *
 * Provides route data for: useNavigation (sidebar), SettingsPanel,
 * CommandPalette, and ProductGate (via getRouteProductKey).
 *
 * Does NOT own rendering logic or hook-level state (feature flags,
 * entitlements, pathname). Consumers import the data and apply their
 * own runtime context.
 *
 * Depends on: lib/entitlements/config (ProductKey, FeatureKey, PRODUCT_KEYS, FEATURES)
 */
import {
  Bell,
  Book,
  Boxes,
  Building2,
  Cable,
  Calendar,
  CheckSquare,
  Clock,
  Contact,
  DollarSign,
  Grid2X2,
  Handshake,
  Home,
  Landmark,
  Link2,
  Megaphone,
  Palette,
  Phone,
  ReceiptText,
  Rocket,
  Settings2,
  UserStar,
  Wrench,
  Zap,
} from "lucide-react";

import CrewMembersIcon from "@/components/icons/CrewMembersIcon";
import { POSTHOG_FEATURE_FLAGS } from "@/lib/posthog/feature-flags";

import type { LucideIcon } from "lucide-react";
import type { FeatureKey, ProductKey } from "@/lib/entitlements/config";

// ---------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------

type SettingsGroup = "Account" | "Company";
type SettingsSubgroup = "CRM" | "Marketing" | "System";

export interface RouteMatcher {
  pattern: string;
  matchType: "includes" | "endsWith" | "startsWith";
  /** pathname must NOT include this string for the match to succeed */
  exclude?: string;
}

export interface RouteAccess {
  productsAnyOf?: readonly ProductKey[];
  featureKey?: FeatureKey;
  paidOnly?: boolean;
}

export interface AppRoute {
  key: string;
  pathTemplate: string;

  gateSegment?: string;
  access?: RouteAccess;

  activeMatchers: RouteMatcher[];

  settings?: {
    group: SettingsGroup;
    subgroup?: SettingsSubgroup;
    label: string;
    icon: LucideIcon;
    activeMatchers: RouteMatcher[];
    isExternal?: boolean;
    featureFlag?: string;
    order: number;
  };

  search?: {
    name: string;
    description: string;
    synonyms: string[];
    /** Specific sub-features on this page — helps the LLM disambiguate when routing. */
    capabilities?: string[];
  };

  conditionalPath?: {
    flag: keyof RouteContext;
    whenFalse: string;
  };

  /** When set, activeMatchers only evaluate to true when this RouteContext flag is truthy */
  activeWhenFlag?: keyof RouteContext;

  /** When true, this route is hidden from search/navigation for agency orgs */
  agencyHidden?: boolean;

  /** When true, this route only appears in navigation for super users */
  superUserOnly?: boolean;
}

export interface NavItem {
  key: string;
  label: string;
  shortLabel?: string;
  description: string;
  icon: LucideIcon;
  sectionKey: string;

  routeKeys: string[];

  productKey?: ProductKey;

  /** PostHog flag key. When set, this nav item is hidden until the flag is enabled. */
  featureFlag?: string;
  agencyHidden?: boolean;
  requiresAnySubscription?: boolean;
  requiresShowCatchUp?: boolean;
  /**
   * When true, only callers who can act as admins on the current website see
   * this item: SuperUsers (Clerk impersonation / `isSuperUser` claim), or
   * users whose Clerk org membership role is `org:admin` (which the team-member
   * sync maps to `TeamMemberRole.ADMIN` / `OWNER` server-side). Mirrors the
   * `resolveAdminCaller` gate enforced by the corresponding API routes — the
   * sidebar should not advertise pages the API will 403 on.
   */
  requiresAdminAccess?: boolean;

  onboardingLink?: string;
  alsoExpandKeys?: string[];
  isBeta?: boolean;
  order: number;
}

export interface GatedPlaceholder {
  productKey: ProductKey;
  sectionKey: string;
  key: string;
  name: string;
  shortName: string;
  description: string;
  icon: LucideIcon;
}

// ---------------------------------------------------------------------------
// section keys — stable identifiers for sidebar divider grouping
// ---------------------------------------------------------------------------

export const SECTION_KEYS = {
  TOP: "top",
  CRM: "crm",
  MARKETING_KIT: "marketing-kit",
  VOICE_ASSISTANT: "voice-assistant",
} as const;

// ---------------------------------------------------------------------------
// APP_ROUTES — every page, including non-nav gated-only routes
// ---------------------------------------------------------------------------

const CRM: ProductKey = "crm";
const MK: ProductKey = "marketing-kit";
const VA: ProductKey = "voice-assistant";

export const APP_ROUTES: readonly AppRoute[] = [
  // =========================================================================
  // TOP section
  // =========================================================================
  {
    key: "home",
    pathTemplate: "/dashboard/sites/{websiteId}/home",
    activeMatchers: [{ pattern: "/home", matchType: "includes" }],
    conditionalPath: {
      flag: "showHomePage",
      whenFalse: "/dashboard/sites/{websiteId}/overview",
    },
    search: {
      name: "Home",
      description: "Home",
      synonyms: ["home", "dashboard"],
    },
  },
  {
    key: "catch-up",
    pathTemplate: "/dashboard/sites/{websiteId}/catch-up",
    gateSegment: "catch-up",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [{ pattern: "/catch-up", matchType: "includes" }],
    search: {
      name: "Catch Up",
      description: "Triage leads and items needing attention",
      synonyms: ["triage", "inbox", "todo"],
    },
  },

  // =========================================================================
  // CRM section — Clients (ungated, requires any subscription)
  // =========================================================================
  {
    key: "clients",
    pathTemplate: "/dashboard/sites/{websiteId}/clients",
    activeMatchers: [{ pattern: "/clients", matchType: "includes" }],
    search: {
      name: "Clients",
      description: "View and manage your clients",
      synonyms: ["contacts", "leads", "customers"],
    },
  },

  // =========================================================================
  // CRM section — gated routes
  // =========================================================================
  {
    key: "schedule",
    pathTemplate: "/dashboard/sites/{websiteId}/schedule",
    gateSegment: "schedule",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [{ pattern: "/schedule", matchType: "includes" }],
    search: {
      name: "Schedule",
      description: "View and manage appointments",
      synonyms: ["calendar", "appointments", "bookings", "timeslots", "availability"],
    },
  },
  {
    key: "timesheets",
    pathTemplate: "/dashboard/sites/{websiteId}/timesheets",
    gateSegment: "timesheets",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [{ pattern: "/timesheets", matchType: "includes" }],
    search: {
      name: "Timesheets",
      description: "Review weekly clock-in hours for your crew",
      synonyms: ["time tracking", "clock in", "clock out", "hours", "payroll"],
      capabilities: [
        "weekly grid of hours per crew member",
        "highlight days over 12 hours or with notes/adjustments",
        "export weekly timesheets as CSV",
      ],
    },
  },
  {
    key: "quotes",
    pathTemplate: "/dashboard/sites/{websiteId}/quotes",
    gateSegment: "quotes",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [{ pattern: "/quotes", matchType: "includes" }],
    search: {
      name: "Quotes",
      description: "View and manage your quotes library",
      synonyms: ["estimates", "proposals"],
    },
  },
  {
    key: "jobs",
    pathTemplate: "/dashboard/sites/{websiteId}/jobs",
    gateSegment: "jobs",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [{ pattern: "/jobs", matchType: "includes" }],
    search: {
      name: "Jobs",
      description: "View and manage your jobs",
      synonyms: ["work orders", "tasks"],
    },
  },
  {
    key: "invoices",
    pathTemplate: "/dashboard/sites/{websiteId}/invoices",
    gateSegment: "invoices",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [{ pattern: "/invoices", matchType: "includes" }],
    search: {
      name: "Invoices",
      description: "View and manage your invoices",
      synonyms: ["billing", "payments"],
    },
  },
  {
    key: "payments",
    pathTemplate: "/dashboard/sites/{websiteId}/payments",
    gateSegment: "payments",
    access: { productsAnyOf: [CRM], featureKey: "payments" satisfies FeatureKey, paidOnly: true },
    activeMatchers: [{ pattern: "/payments", matchType: "includes" }],
    search: {
      name: "Payments",
      description: "View your payment status and settings",
      synonyms: ["stripe", "billing"],
      capabilities: ["Stripe account connection", "payment method configuration", "payout schedule and history"],
    },
  },
  {
    key: "automations",
    pathTemplate: "/dashboard/sites/{websiteId}/automations",
    gateSegment: "automations",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [{ pattern: "/automations", matchType: "includes" }],
    search: {
      name: "Automations",
      description: "Manage automated workflows and triggers",
      synonyms: ["workflows", "triggers", "rules"],
    },
  },

  // =========================================================================
  // CRM — non-nav gated routes (gating only, no sidebar/settings/search)
  // =========================================================================
  {
    key: "messages",
    pathTemplate: "/dashboard/sites/{websiteId}/messages",
    gateSegment: "messages",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [],
  },
  {
    key: "pricebook",
    pathTemplate: "/dashboard/sites/{websiteId}/pricebook",
    gateSegment: "pricebook",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [],
    settings: {
      group: "Company",
      subgroup: "CRM",
      label: "Pricebook",
      icon: Book,
      activeMatchers: [{ pattern: "/pricebook", matchType: "endsWith" }],
      order: 0,
    },
    search: {
      name: "Pricebook",
      description: "Manage your line items and pricing",
      synonyms: ["pricing", "line items", "rates", "catalog"],
      capabilities: [
        "create and edit line items",
        "set prices, unit costs, and markup",
        "organize items into categories",
      ],
    },
  },
  {
    key: "crm-settings",
    pathTemplate: "/dashboard/sites/{websiteId}/crm-settings",
    gateSegment: "crm-settings",
    access: { productsAnyOf: [CRM] },
    activeMatchers: [],
    settings: {
      group: "Company",
      subgroup: "CRM",
      label: "Settings",
      icon: Settings2,
      activeMatchers: [{ pattern: "/crm-settings", matchType: "endsWith" }],
      order: 1,
    },
    search: {
      name: "CRM Settings",
      description: "Configure CRM preferences and defaults",
      synonyms: ["crm config", "crm preferences"],
      capabilities: ["default quote and invoice settings", "lead pipeline stages", "job workflow configuration"],
    },
  },

  // =========================================================================
  // Voice Assistant
  // =========================================================================
  {
    key: "ai-phone",
    pathTemplate: "/dashboard/sites/{websiteId}/ai-phone/calls",
    gateSegment: "ai-phone",
    access: { productsAnyOf: [VA] },
    activeMatchers: [{ pattern: "/ai-phone", matchType: "includes" }],
    search: {
      name: "AI Assistant",
      description: "Configure call tracking and AI assistant",
      synonyms: ["voice", "phone", "calls"],
    },
  },

  // =========================================================================
  // Marketing Kit — Website accordion children
  // =========================================================================
  {
    key: "overview",
    pathTemplate: "/dashboard/sites/{websiteId}/overview",
    activeMatchers: [{ pattern: "/overview", matchType: "includes" }],
    activeWhenFlag: "showHomePage",
    search: {
      name: "Overview",
      description: "Website overview and analytics",
      synonyms: ["analytics", "stats", "website"],
    },
  },
  {
    key: "pages",
    pathTemplate: "/dashboard/sites/{websiteId}/pages",
    gateSegment: "pages",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/pages", matchType: "includes" }],
    search: {
      name: "Site Editor",
      description: "Edit your website with our easy-to-use editor.",
      synonyms: ["editor", "website", "site", "pages"],
    },
  },
  {
    key: "services",
    pathTemplate: "/dashboard/sites/{websiteId}/services",
    gateSegment: "services",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/services", matchType: "includes" }],
    search: {
      name: "Services & Areas",
      description: "Adjust your services and areas of expertise",
      synonyms: ["services", "areas"],
    },
    settings: {
      group: "Company",
      label: "Services & Areas",
      icon: Boxes,
      activeMatchers: [{ pattern: "/services", matchType: "endsWith" }],
      order: 2,
    },
  },
  {
    key: "photos",
    pathTemplate: "/dashboard/sites/{websiteId}/photos",
    gateSegment: "photos",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/photos", matchType: "includes" }],
    search: {
      name: "Photos",
      description: "Manage the media for your website",
      synonyms: ["image", "media", "pictures", "gallery", "photos"],
    },
  },
  {
    key: "blogs",
    pathTemplate: "/dashboard/sites/{websiteId}/blogs",
    gateSegment: "blogs",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/blogs", matchType: "includes" }],
    search: {
      name: "Blogs",
      description: "Demonstrate your work via blog posts",
      synonyms: ["blog", "article", "content", "posts"],
    },
  },
  {
    key: "projects",
    pathTemplate: "/dashboard/sites/{websiteId}/projects",
    gateSegment: "projects",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/projects", matchType: "includes" }],
    search: {
      name: "Projects",
      description: "Showcase your work via location tagged projects",
      synonyms: ["portfolio", "work", "showcase"],
    },
  },
  {
    key: "testimonials",
    pathTemplate: "/dashboard/sites/{websiteId}/testimonials",
    gateSegment: "testimonials",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/testimonials", matchType: "includes" }],
    search: {
      name: "Testimonials",
      description: "Demonstrate social proof via testimonials",
      synonyms: ["reviews", "feedback", "social proof"],
    },
  },
  {
    key: "goals",
    pathTemplate: "/dashboard/sites/{websiteId}/goals",
    gateSegment: "goals",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/goals", matchType: "includes" }],
    search: {
      name: "Lead Capture",
      description: "Goals and Links & QR Codes",
      synonyms: ["cta", "conversion", "goals"],
    },
  },
  {
    key: "data-trends",
    pathTemplate: "/dashboard/sites/{websiteId}/data-trends",
    gateSegment: "data-trends",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/data-trends", matchType: "includes" }],
    superUserOnly: true,
    search: {
      name: "Data Trends",
      description: "Search performance and indexing trends from Google",
      synonyms: ["gsc", "seo", "indexing", "impressions", "search console"],
    },
  },
  {
    key: "website-settings",
    pathTemplate: "/dashboard/sites/{websiteId}/settings",
    gateSegment: "settings",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/settings", matchType: "endsWith", exclude: "/ai-phone" }],
    search: {
      name: "Website Settings",
      description: "Adjust your domain, scripts, redirects, and more",
      synonyms: ["domain", "scripts", "redirects"],
      capabilities: [
        "custom domain configuration",
        "URL redirects",
        "SEO and search engine visibility",
        "Google Search Console integration",
        "tracking scripts (GTM, Google Analytics, Google Ads, CallRail, Meta Pixel)",
        "custom CSS and custom scripts",
        "external webhook URL",
        "call sync settings",
      ],
    },
    settings: {
      group: "Company",
      subgroup: "Marketing",
      label: "Website Settings",
      icon: Settings2,
      activeMatchers: [{ pattern: "/settings", matchType: "endsWith" }],
      order: 0,
    },
  },

  // =========================================================================
  // Marketing Kit — Social Media
  // =========================================================================
  {
    key: "socials",
    pathTemplate: "/dashboard/sites/{websiteId}/socials",
    gateSegment: "socials",
    access: { productsAnyOf: [MK] },
    activeMatchers: [
      { pattern: "/socials", matchType: "endsWith" },
      { pattern: "/socials-settings", matchType: "includes" },
    ],
    search: {
      name: "Posts",
      description: "Your social media posts",
      synonyms: ["smm", "social"],
    },
  },

  // =========================================================================
  // Marketing Kit — Reviews
  // =========================================================================
  {
    key: "reviews-overview",
    pathTemplate: "/dashboard/sites/{websiteId}/reviews",
    gateSegment: "reviews",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/reviews", matchType: "endsWith" }],
    search: {
      name: "Overview",
      description: "Review performance and stats",
      synonyms: ["reviews", "ratings"],
    },
  },
  {
    key: "reviews-manager",
    pathTemplate: "/dashboard/sites/{websiteId}/reviews/manager",
    gateSegment: "reviews",
    access: { productsAnyOf: [MK] },
    activeMatchers: [
      { pattern: "/manager", matchType: "endsWith" },
      { pattern: "/reply-flow", matchType: "endsWith" },
    ],
    search: {
      name: "Review Manager",
      description: "Collect and reply to all client feedback",
      synonyms: ["reply", "feedback", "manage reviews"],
    },
  },

  // =========================================================================
  // Marketing Kit — GBP Optimizer
  // =========================================================================
  {
    key: "gbp-optimizer",
    pathTemplate: "/dashboard/sites/{websiteId}/gbp-optimizer",
    gateSegment: "gbp-optimizer",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/gbp-optimizer", matchType: "includes" }],
    search: {
      name: "GBP Optimizer",
      description: "View your profile score and steps to improve",
      synonyms: ["google business", "gbp", "profile"],
    },
  },
  {
    key: "gbp-analytics",
    pathTemplate: "/dashboard/sites/{websiteId}/gbp-analytics",
    gateSegment: "gbp-analytics",
    access: { productsAnyOf: [MK] },
    activeMatchers: [{ pattern: "/gbp-analytics", matchType: "includes" }],
    search: {
      name: "Analytics",
      description: "View your Google Business Profile performance",
      synonyms: ["google business", "gbp", "performance"],
    },
  },

  // =========================================================================
  // Marketing Kit — non-nav gated routes
  // =========================================================================
  {
    key: "blog-post",
    pathTemplate: "/dashboard/sites/{websiteId}/blog-post",
    gateSegment: "blog-post",
    access: { productsAnyOf: [MK] },
    activeMatchers: [],
  },
  {
    key: "social-media",
    pathTemplate: "/dashboard/sites/{websiteId}/social-media",
    gateSegment: "social-media",
    access: { productsAnyOf: [MK] },
    activeMatchers: [],
  },
  {
    key: "socials-settings",
    pathTemplate: "/dashboard/sites/{websiteId}/socials-settings",
    gateSegment: "socials-settings",
    access: { productsAnyOf: [MK] },
    activeMatchers: [],
    agencyHidden: true,
    search: {
      name: "Social Media Settings",
      description: "Configure social media accounts and preferences",
      synonyms: ["smm settings", "social config", "social accounts"],
    },
  },
  {
    key: "links",
    pathTemplate: "/dashboard/sites/{websiteId}/links",
    gateSegment: "links",
    access: { productsAnyOf: [MK] },
    activeMatchers: [],
    settings: {
      group: "Company",
      subgroup: "System",
      label: "Links & QR Codes",
      icon: Link2,
      activeMatchers: [{ pattern: "/links", matchType: "endsWith" }],
      order: 1,
    },
    search: {
      name: "Links & QR Codes",
      description: "Manage your tracking links and QR codes",
      synonyms: ["qr", "short links", "tracking", "utm"],
    },
  },

  // =========================================================================
  // Settings-only routes (no sidebar, no gating)
  // =========================================================================
  {
    key: "companies",
    pathTemplate: "/dashboard/sites/{websiteId}/companies",
    activeMatchers: [],
    settings: {
      group: "Account",
      label: "Companies",
      icon: Building2,
      activeMatchers: [{ pattern: "/companies", matchType: "endsWith" }],
      order: 0,
    },
    search: {
      name: "Companies",
      description: "View and search websites in your organization",
      synonyms: ["businesses", "sites", "organizations", "accounts"],
      capabilities: ["search organization websites", "add new business", "transfer website to new organization"],
    },
  },
  {
    key: "company",
    pathTemplate: "/dashboard/sites/{websiteId}/company",
    activeMatchers: [],
    settings: {
      group: "Company",
      label: "Company Info",
      icon: Building2,
      activeMatchers: [{ pattern: "/company", matchType: "endsWith" }],
      order: 0,
    },
    search: {
      name: "Company Info",
      description: "Edit your company name, address, and contact details",
      synonyms: ["business info", "company name", "address", "phone"],
      capabilities: [
        "company name",
        "industry",
        "Google rating and Google Place ID",
        "business address and location",
        "phone number and email",
        "timezone",
        "hours of operation",
        "social media links (Facebook, Instagram, Twitter, YouTube, etc.)",
        "extra business info (license, insurance, warranty, year established, about)",
      ],
    },
  },
  {
    key: "brand",
    pathTemplate: "/dashboard/sites/{websiteId}/brand",
    activeMatchers: [],
    settings: {
      group: "Company",
      label: "Branding",
      icon: Palette,
      activeMatchers: [{ pattern: "/brand", matchType: "endsWith" }],
      order: 1,
    },
    search: {
      name: "Branding",
      description: "Customize your brand colors, fonts, and logo",
      synonyms: ["style", "theme", "look", "colors", "fonts", "logo"],
      capabilities: [
        "logo and icon upload",
        "brand colors (primary, secondary)",
        "font selection (heading and body fonts)",
        "shape style (rounded vs sharp corners)",
      ],
    },
  },
  {
    key: "crew",
    pathTemplate: "/dashboard/sites/{websiteId}/crew",
    activeMatchers: [],
    settings: {
      group: "Account",
      label: "Crew Members",
      icon: CrewMembersIcon,
      activeMatchers: [
        { pattern: "/crew", matchType: "endsWith" },
        { pattern: "/crew/", matchType: "includes" },
      ],
      order: 1,
    },
    search: {
      name: "Crew members",
      description: "Manage your team members",
      synonyms: ["team", "members", "users", "staff"],
      capabilities: ["invite and remove team members", "assign roles and permissions"],
    },
  },
  {
    key: "connections",
    pathTemplate: "/dashboard/sites/{websiteId}/connections",
    activeMatchers: [],
    settings: {
      group: "Company",
      subgroup: "System",
      label: "Connections",
      icon: Cable,
      activeMatchers: [{ pattern: "/connections", matchType: "endsWith" }],
      featureFlag: POSTHOG_FEATURE_FLAGS.QBO_CONNECTIONS,
      order: 3,
    },
    search: {
      name: "Connections",
      description: "Connect external services like QuickBooks",
      synonyms: ["quickbooks", "qbo", "connections", "accounting"],
    },
  },
  {
    key: "notification-settings",
    pathTemplate: "/dashboard/sites/{websiteId}/notification-settings",
    activeMatchers: [],
    settings: {
      group: "Company",
      subgroup: "System",
      label: "Notifications",
      icon: Bell,
      activeMatchers: [{ pattern: "/notification-settings", matchType: "endsWith" }],
      order: 0,
    },
    search: {
      name: "Notifications",
      description: "Configure email and push notification preferences",
      synonyms: ["email", "alerts", "notification settings"],
      capabilities: ["email and SMS lead notifications", "project and review email alerts"],
    },
  },
  {
    key: "partners",
    pathTemplate: "https://rebolthq.com/partners",
    activeMatchers: [],
    settings: {
      group: "Company",
      subgroup: "System",
      label: "Partners",
      icon: Handshake,
      activeMatchers: [],
      isExternal: true,
      order: 2,
    },
  },
] as const satisfies readonly AppRoute[];

// ---------------------------------------------------------------------------
// NAV_PRESENTATION — sidebar presentation layer
//
// These define *how* routes appear in the sidebar, not the routes themselves.
// A NavItem groups one or more APP_ROUTES keys under a single sidebar entry
// with its own icon, label, section, and visibility rules (feature flags,
// agency hiding, subscription guards).
//
// Intentionally separate from APP_ROUTES because the concerns differ:
//   - APP_ROUTES owns the URL, gating segment, page-level access, search
//     metadata, and active-state matching. One entry per navigable page.
//   - NAV_PRESENTATION owns the sidebar UX: accordion grouping (WEBSITE
//     bundles 9 routes), display order, collapse behavior, onboarding
//     fallback links, and feature-flag gating for gradual rollout.
//
// Multi-route items (WEBSITE, REVIEWS, GBP) genuinely need their own label
// and description — "Website" is not the same concept as any single child
// route. Single-route items carry some string overlap with their route's
// search metadata; that's an acceptable trade-off for keeping the two
// layers independently evolvable.
// ---------------------------------------------------------------------------

export const NAV_PRESENTATION: readonly NavItem[] = [
  {
    key: "HOME",
    label: "Home",
    shortLabel: "Home",
    description: "Home",
    icon: Home,
    sectionKey: SECTION_KEYS.TOP,
    routeKeys: ["home"],
    order: 0,
  },
  {
    key: "CATCH_UP",
    label: "Catch Up",
    shortLabel: "Catch Up",
    description: "Triage leads and items needing attention",
    icon: CheckSquare,
    sectionKey: SECTION_KEYS.TOP,
    routeKeys: ["catch-up"],
    requiresShowCatchUp: true,
    order: 1,
  },
  {
    key: "CLIENTS",
    label: "Clients",
    shortLabel: "Clients",
    description: "View and manage your clients",
    icon: Contact,
    sectionKey: SECTION_KEYS.CRM,
    routeKeys: ["clients"],
    requiresAnySubscription: true,
    order: 2,
  },
  {
    key: "SCHEDULE",
    label: "Schedule",
    shortLabel: "Schedule",
    description: "View and manage appointments",
    icon: Calendar,
    sectionKey: SECTION_KEYS.CRM,
    routeKeys: ["schedule"],
    productKey: CRM,
    order: 3,
  },
  {
    key: "QUOTES",
    label: "Quotes",
    shortLabel: "Quotes",
    description: "View and manage your quotes",
    icon: ReceiptText,
    sectionKey: SECTION_KEYS.CRM,
    routeKeys: ["quotes"],
    productKey: CRM,
    order: 4,
  },
  {
    key: "JOBS",
    label: "Jobs",
    shortLabel: "Jobs",
    description: "View and manage your jobs",
    icon: Wrench,
    sectionKey: SECTION_KEYS.CRM,
    routeKeys: ["jobs"],
    productKey: CRM,
    order: 5,
  },
  {
    key: "INVOICES",
    label: "Invoices",
    shortLabel: "Invoices",
    description: "View and manage your invoices",
    icon: DollarSign,
    sectionKey: SECTION_KEYS.CRM,
    routeKeys: ["invoices"],
    productKey: CRM,
    order: 6,
  },
  {
    key: "PAYMENTS",
    label: "Payments",
    shortLabel: "Payments",
    description: "View your payment status and settings",
    icon: Landmark,
    sectionKey: SECTION_KEYS.CRM,
    routeKeys: ["payments"],
    productKey: CRM,
    order: 7,
  },
  {
    key: "AUTOMATIONS",
    label: "Automations",
    shortLabel: "Automations",
    description: "Manage automated workflows and triggers",
    icon: Zap,
    sectionKey: SECTION_KEYS.CRM,
    routeKeys: ["automations"],
    productKey: CRM,
    order: 8,
  },
  {
    key: "TIMESHEETS",
    label: "Timesheets",
    shortLabel: "Timesheets",
    description: "Review weekly clock-in hours for your crew",
    icon: Clock,
    sectionKey: SECTION_KEYS.CRM,
    routeKeys: ["timesheets"],
    productKey: CRM,
    featureFlag: POSTHOG_FEATURE_FLAGS.TIMESHEETS_DESKTOP_V1,
    requiresAdminAccess: true,
    order: 8.5,
  },
  {
    key: "AI_ASSISTANT",
    label: "AI Assistant",
    shortLabel: "AI Assistant",
    description: "AI-powered call tracking, recording, and voice assistant",
    icon: Phone,
    sectionKey: SECTION_KEYS.VOICE_ASSISTANT,
    routeKeys: ["ai-phone"],
    productKey: VA,
    isBeta: true,
    order: 9,
  },
  {
    key: "WEBSITE",
    label: "Website",
    shortLabel: "Website",
    description: "Build and manage your service business website",
    icon: Grid2X2,
    sectionKey: SECTION_KEYS.MARKETING_KIT,
    routeKeys: [
      "overview",
      "pages",
      "services",
      "photos",
      "blogs",
      "projects",
      "testimonials",
      "goals",
      "data-trends",
      "website-settings",
    ],
    productKey: MK,
    onboardingLink: "/dashboard/sites/new",
    order: 10,
  },
  {
    key: "SOCIAL_MEDIA",
    label: "Social Media Manager",
    shortLabel: "Social Media",
    description: "Manage your social media posts",
    icon: Megaphone,
    sectionKey: SECTION_KEYS.MARKETING_KIT,
    routeKeys: ["socials"],
    productKey: MK,
    agencyHidden: true,
    onboardingLink: "/dashboard/sites/new",
    order: 11,
  },
  {
    key: "REVIEWS",
    label: "Reviews",
    shortLabel: "Reviews",
    description: "Manage your reviews",
    icon: UserStar,
    sectionKey: SECTION_KEYS.MARKETING_KIT,
    routeKeys: ["reviews-overview", "reviews-manager"],
    productKey: MK,
    order: 12,
  },
  {
    key: "GBP",
    label: "GBP Optimizer",
    shortLabel: "GBP Optimizer",
    description: "Optimize your Google Business Profile",
    icon: Rocket,
    sectionKey: SECTION_KEYS.MARKETING_KIT,
    routeKeys: ["gbp-optimizer", "gbp-analytics"],
    productKey: MK,
    agencyHidden: true,
    order: 13,
  },
] as const satisfies readonly NavItem[];

// ---------------------------------------------------------------------------
// GATED_PLACEHOLDERS — exact sidebar copy for collapsed product sections
// ---------------------------------------------------------------------------

export const GATED_PLACEHOLDERS: readonly GatedPlaceholder[] = [
  {
    productKey: CRM,
    sectionKey: SECTION_KEYS.CRM,
    key: "CRM_GATED",
    name: "CRM",
    shortName: "CRM",
    description: "Scheduling, quotes, invoices, and more",
    icon: Grid2X2,
  },
  {
    productKey: VA,
    sectionKey: SECTION_KEYS.VOICE_ASSISTANT,
    key: "VOICE_ASSISTANT_GATED",
    name: "AI Voice Assistant",
    shortName: "AI Assistant",
    description: "AI-powered call answering for your business",
    icon: Phone,
  },
  {
    productKey: MK,
    sectionKey: SECTION_KEYS.MARKETING_KIT,
    key: "MARKETING_KIT_GATED",
    name: "Marketing Kit",
    shortName: "Marketing Kit",
    description: "Website, SEO, social media, and more",
    icon: Grid2X2,
  },
] as const satisfies readonly GatedPlaceholder[];

// ---------------------------------------------------------------------------
// lookup indexes — built once at module load
// ---------------------------------------------------------------------------

const ROUTE_BY_KEY: ReadonlyMap<string, AppRoute> = new Map(APP_ROUTES.map((r) => [r.key, r]));

const GATE_SEGMENT_INDEX: ReadonlyMap<string, AppRoute> = new Map(
  APP_ROUTES.filter((r) => r.gateSegment).map((r) => [r.gateSegment!, r])
);

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

export function getRouteByKey(key: string): AppRoute | undefined {
  return ROUTE_BY_KEY.get(key);
}

export function getRoutesForKeys(keys: readonly string[]): AppRoute[] {
  return keys.map((k) => ROUTE_BY_KEY.get(k)).filter((r): r is AppRoute => r != null);
}

/**
 * Returns the single product that gates a route for ProductGate callers.
 * Throws if a route declares multiple products in productsAnyOf — forces
 * the callsite to use productsAnyOf directly for multi-product gating.
 */
export function getPrimaryGateProduct(route: AppRoute): ProductKey | null {
  const products = route.access?.productsAnyOf;
  if (!products?.length) return null;
  if (products.length > 1) {
    throw new Error(`Route "${route.key}" has multiple gate products — use productsAnyOf directly`);
  }
  return products[0];
}

/**
 * Resolves the product key that gates a given pathname, or null if ungated.
 * Drop-in replacement for the former ROUTE_PRODUCT_MAP-backed implementation.
 */
export function getRouteProductKeyFromRegistry(pathname: string): ProductKey | null {
  const match = pathname.match(/\/dashboard\/sites\/[^/]+\/([^/]+)/);
  if (!match) return null;
  const route = GATE_SEGMENT_INDEX.get(match[1]);
  if (!route) return null;
  return getPrimaryGateProduct(route);
}

/**
 * Evaluates a single RouteMatcher against a pathname.
 */
export function evaluateMatcher(matcher: RouteMatcher, pathname: string): boolean {
  if (matcher.exclude && pathname.includes(matcher.exclude)) return false;
  switch (matcher.matchType) {
    case "includes":
      return pathname.includes(matcher.pattern);
    case "endsWith":
      return pathname.endsWith(matcher.pattern);
    case "startsWith":
      return pathname.startsWith(matcher.pattern);
    default:
      return false;
  }
}

/**
 * Returns true if any matcher in the array matches the pathname.
 */
export function evaluateMatchers(matchers: readonly RouteMatcher[], pathname: string): boolean {
  return matchers.some((m) => evaluateMatcher(m, pathname));
}

/**
 * Checks if a pathname is a settings route by testing against all routes
 * that have a settings entry.
 */
export function isSettingsRouteFromRegistry(pathname: string): boolean {
  return APP_ROUTES.some(
    (route) => route.settings && !route.settings.isExternal && evaluateMatchers(route.settings.activeMatchers, pathname)
  );
}

export type SettingsRoute = AppRoute & { settings: NonNullable<AppRoute["settings"]> };

/**
 * Returns all routes that have settings metadata, grouped by settings.group.
 */
export function getSettingsRoutesByGroup(): Map<string, SettingsRoute[]> {
  const groups = new Map<string, SettingsRoute[]>();
  for (const route of APP_ROUTES) {
    if (!route.settings) continue;
    const group = route.settings.group;
    const arr = groups.get(group) ?? [];
    arr.push(route as SettingsRoute);
    groups.set(group, arr);
  }
  for (const [, routes] of groups) {
    routes.sort((a, b) => a.settings.order - b.settings.order);
  }
  return groups;
}

// ---------------------------------------------------------------------------
// route resolution
// ---------------------------------------------------------------------------

export interface RouteContext {
  websiteId: string | undefined;
  showHomePage: boolean;
  fallbackHref?: string;
}

export interface ResolvedRoute {
  key: string;
  href: string;
  name: string;
  description: string;
}

/**
 * Resolves a route's pathTemplate into a concrete href given the current context.
 */
export function resolveRoutePath(route: AppRoute, ctx: RouteContext): string {
  let template = route.pathTemplate;

  if (route.conditionalPath) {
    if (!ctx[route.conditionalPath.flag]) {
      template = route.conditionalPath.whenFalse;
    }
  }

  if (!ctx.websiteId) {
    return ctx.fallbackHref ?? "/dashboard/sites/new";
  }

  return template.replace("{websiteId}", String(ctx.websiteId));
}

/**
 * Evaluates whether a route is "current" (active) for the given pathname,
 * respecting conditionalPath context.
 */
export function isRouteActive(route: AppRoute, pathname: string, ctx: RouteContext): boolean {
  if (route.activeWhenFlag) {
    if (!ctx[route.activeWhenFlag]) return false;
  }

  if (route.conditionalPath) {
    if (!ctx[route.conditionalPath.flag] && route.activeMatchers.length > 0) {
      const altTemplate = route.conditionalPath.whenFalse;
      const altSegment = altTemplate.split("/").pop();
      if (altSegment) {
        return pathname.includes(`/${altSegment}`);
      }
    }
  }
  return evaluateMatchers(route.activeMatchers, pathname);
}

// ---------------------------------------------------------------------------
// AI chat context — formats all searchable routes for the LLM system prompt
// ---------------------------------------------------------------------------

/**
 * `origin` (e.g. `https://rebolthq.com`) makes every href absolute for
 * surfaces that cannot resolve app-relative paths. Omit it for the web app,
 * where relative hrefs keep the per-site prompt bytes unchanged.
 */
export function buildRouteContext(websiteId: string, options: { origin?: string } = {}): string {
  const prefix = options.origin ?? "";
  const lines = APP_ROUTES.filter((r) => r.search).map((r) => {
    const href = `${prefix}${r.pathTemplate.replace("{websiteId}", websiteId)}`;
    let line = `- ${r.search!.name}: ${r.search!.description} -> ${href}`;
    if (r.search!.capabilities?.length) {
      line += `\n  Features: ${r.search!.capabilities.join("; ")}`;
    }
    return line;
  });
  return `Available pages in Rebolt web app:\n${lines.join("\n")}`;
}
